// Absolute path to the root of the SPA, honouring the deploy prefix
// (package.json "homepage" / REACT_APP_PUBLIC_URL).
//
// Use this instead of a hardcoded '/' for full-page navigations back to the
// start of the journey. A bare '/' escapes the prefix and lands the user on
// the CloudFront root — outside the app, blank page, journey lost. These are
// full-page assignments to window.location, so they bypass the router and its
// basename (see src/lib/history.js) entirely.
//
// Trailing slash is deliberate: '/open-demat-account' (no slash) is not the
// same request as '/open-demat-account/' to the origin.
const homeUrl = `${process.env.REACT_APP_PUBLIC_URL || ''}/`;

// Absolute URL for an in-app route, on the origin the user actually loaded the
// app from, e.g. absoluteUrl('/dad/verifyEsign').
//
// For return URLs we hand to the backend to pass on to a third party (OneMoney,
// the eSign vendors). The origin MUST be read at runtime: the same deployment is
// reachable on several hosts — directly (ekyc.dev.motilaloswal.cloud,
// ekycv3.motilaloswal.com) and through reverse proxies
// (webadobe.motilaloswaluat.com, www.motilaloswal.com) — so it is only known
// from where the current page was served. A hardcoded host sends the customer
// back to a different origin than they started on, which means different
// sessionStorage and cookies: their JWT and journey are gone.
//
// This is why the backend can no longer own these URLs (it used to, via
// ExternalProviders:OneMoney:RedirectUrl and ESign:FrontendReturnUrl). BE
// allowlist-validates the origin and falls back to its config when we send
// nothing, so this is safe to deploy before or after the backend change.
export function absoluteUrl(route) {
    const suffix = route ? `/${String(route).replace(/^\/+/, '')}` : '';
    return `${window.location.origin}${process.env.REACT_APP_PUBLIC_URL || ''}${suffix}`;
}

// The current pathname with the deploy prefix stripped — i.e. what the ROUTER
// sees, and what app route literals are written against.
//
// ALWAYS use this instead of raw `window.location.pathname` when comparing
// against a route literal ('/dad/...') or a routeForJourney() output. The two
// are in different coordinate systems and it is easy to miss:
//   window.location.pathname -> '/open-demat-account/dad/pan'   (RAW, has prefix)
//   useLocation().pathname   -> '/dad/pan'                      (router, stripped)
// A raw-vs-literal comparison silently evaluates false once the app is served
// under a prefix, which disables the guard rather than erroring — see
// src/index.js (OneMoney iframe break-out) and dadRejectionCorrection.
//
// Prefer the router's own location (useLocation / props.location) where it's
// available; this exists for code that runs before React mounts, or that must
// deliberately read the live URL rather than a mount-time closure.
export function appPathname(rawPathname) {
    const path = rawPathname != null ? String(rawPathname) : window.location.pathname;
    const base = process.env.REACT_APP_PUBLIC_URL || '';
    if (!base) return path;

    const lowerPath = path.toLowerCase();
    const lowerBase = base.toLowerCase();

    // Match on a SEGMENT BOUNDARY, not a bare prefix: '/open-demat-account-x/dad'
    // starts with '/open-demat-account' but is a different mount, and a naive
    // slice would mangle it into '-x/dad'. Only an exact match or a following
    // '/' counts.
    if (lowerPath === lowerBase) return '/';
    if (lowerPath.indexOf(`${lowerBase}/`) === 0) return path.slice(base.length);
    return path;
}

export default homeUrl;
